import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Apartment } from '@mui/icons-material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { useAtom } from 'jotai';

import { queryClient } from '@/adapters/query';
import {
  collapsibleItemSx,
  DropdownArrow,
  getCollapsibleButtonSx,
  StyledNavButtonOutlined
} from '@/components/Layout/Sidebar';
import { SIDEBAR_COLORS } from '@/components/Layout/sidebarTheme';
import { useCurrentRoute } from '@/hooks/useCurrentRoute';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  ALL_FACILITIES,
  selectedFacilityIdAtom,
  useFacilitiesQuery
} from '@/hooks/useFacilitiesQuery';
import { useSnackbar } from '@/hooks/useSnackbar';
import useIsScreenerAllFacilitiesSelected from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useIsScreenerAllFacilitiesSelected';
import { RoutePath } from '@/routes/RoutePath';

import FacilitySelectorPopover from './FacilitySelectorPopover';
import { getUserFacility } from './getUserFacilityHelper';
import { screenerFacilityChangeSourceAtom } from './screenerFacilityChangeSourceAtom';

const FacilitySelector = ({ expanded }: { expanded: boolean }) => {
  const { showSnackbar } = useSnackbar();

  const isAllFacilitiesSelected = useIsScreenerAllFacilitiesSelected();
  const [previousAllFacilitiesSelected, setPreviousAllFacilitiesSelected] =
    useState(isAllFacilitiesSelected);

  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);
  const currentRoute = useCurrentRoute();
  const [isCRM, setIsCRM] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useAtom(
    selectedFacilityIdAtom
  );
  const [, setFacilityChangeSource] = useAtom(screenerFacilityChangeSourceAtom);
  const currentUser = useCurrentUser().data!;
  const { data: facilitiesResponse, isLoading: isLoadingFacilitiesResponse } =
    useFacilitiesQuery().useFindAllWithCanSelectAllFlag();
  const ref = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const hasMultipleFacilities = currentUser.user_facility_accesses!.length > 1;

  const onSelectFacility = (facilityId: string) => {
    setSelectedFacilityId(facilityId);
    setFacilityChangeSource('manual');
    // Reset all queries except for useCurrentUser to prevent stale data instead
    // of having to invalidate every single query individually
    queryClient.getQueriesData([]).forEach(([queryKey]) => {
      if (queryKey[0] !== 'useCurrentUser') {
        queryClient.resetQueries(queryKey);
      }
    });
    setIsOpen(false);
  };

  const userFacility = useMemo(() => {
    return getUserFacility(
      selectedFacilityId,
      facilitiesResponse,
      currentUser,
      setSelectedFacilityId
    );
  }, [
    selectedFacilityId,
    setSelectedFacilityId,
    facilitiesResponse,
    currentUser
  ]);

  useEffect(() => {
    setIsCRM(location.pathname.startsWith('/crm'));
  }, [location.pathname]);

  useEffect(() => {
    if (location.state?.isFirstLogin) {
      onSelectFacility(currentUser!.facility_id);
    }
  }, [location.state, currentUser]);

  // Detect when "SCREENER_ALL_FACILITIES_PARAM" param changes to false or not
  // being present to alert the user about this change
  useEffect(() => {
    const isSingleLeadView = currentRoute?.includes(
      RoutePath.CRMScreenerSingleLead
    );

    // Only warn if the path actually changed (navigation to different page)
    // Don't warn if just the query param changed on the same page (manual facility switch)
    const didPathChange = prevPathnameRef.current !== location.pathname;

    const shouldWarn =
      previousAllFacilitiesSelected &&
      !isAllFacilitiesSelected &&
      !isSingleLeadView &&
      didPathChange;

    if (shouldWarn) {
      showSnackbar({
        AlertProps: {
          icon: (
            <ErrorOutlineIcon
              sx={{ color: 'warning' }}
              className="MuiAlert-icon"
            />
          )
        },
        severity: 'warning',
        message:
          'All Facilities view is not enabled for this page, your last viewed facility has been selected.'
      });
    }

    // Update refs/state for next comparison
    prevPathnameRef.current = location.pathname;
    setPreviousAllFacilitiesSelected(isAllFacilitiesSelected);
  }, [
    isAllFacilitiesSelected,
    previousAllFacilitiesSelected,
    currentRoute,
    location.pathname
  ]);

  if (isLoadingFacilitiesResponse) {
    return (
      <Stack sx={{ width: '100%', paddingY: 1 }} alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  const isAllFacilities =
    selectedFacilityId === ALL_FACILITIES || isAllFacilitiesSelected;

  const facilityCount = isAllFacilities
    ? (facilitiesResponse?.facilities.length ??
      currentUser.user_facility_accesses?.length)
    : 1;

  const facilityCountLabel =
    facilityCount !== undefined && facilityCount > 99 ? '99+' : facilityCount;

  return (
    <>
      <Tooltip
        title={
          expanded
            ? ''
            : isAllFacilities
              ? 'All Facilities'
              : (userFacility?.name ?? '')
        }
        placement="right"
        disableInteractive
      >
        <span style={{ display: 'inline-flex', width: '100%' }}>
          <StyledNavButtonOutlined
            data-testid="community-selector"
            ref={ref}
            disabled={!hasMultipleFacilities}
            onClick={() => hasMultipleFacilities && setIsOpen(true)}
            sx={getCollapsibleButtonSx(expanded)}
          >
            <ListItemIcon sx={collapsibleItemSx}>
              <Stack alignItems="center">
                <Apartment sx={{ fontSize: '20px' }} />
                {!expanded && facilityCountLabel !== undefined && (
                  <Typography
                    sx={{
                      fontSize: '10px',
                      lineHeight: '14px',
                      fontWeight: 500,
                      color: SIDEBAR_COLORS.textSecondary
                    }}
                  >
                    {facilityCountLabel}
                  </Typography>
                )}
              </Stack>
            </ListItemIcon>
            {expanded && (
              <>
                <ListItemText
                  primary={
                    isAllFacilities ? 'All Facilities' : userFacility?.name
                  }
                  secondary={!isAllFacilities && userFacility?.address}
                />
                {hasMultipleFacilities && <DropdownArrow isOpen={isOpen} />}
              </>
            )}
          </StyledNavButtonOutlined>
        </span>
      </Tooltip>
      <FacilitySelectorPopover
        user={currentUser}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        anchorEl={ref.current!}
        onSelectFacility={onSelectFacility}
        selectedFacilityId={selectedFacilityId}
        isCRM={isCRM}
        canSelectAllFacilities={
          facilitiesResponse?.can_select_all_facilities || false
        }
      />
    </>
  );
};

export default FacilitySelector;
